import { Link } from "@tanstack/react-router";
import { SiteLayout } from "@/components/SiteLayout";
import {
  ArrowRight,
  Check,
  Building2,
  Scale,
  LineChart,
  ShieldCheck,
  Handshake,
  Lightbulb,
  type LucideIcon,
} from "lucide-react";
import { type Locale } from "@/i18n/config";
import { pathFor } from "@/i18n/slugMap";
import { BASE_URL, pageHead } from "@/i18n/head";
import { serviceSchema } from "@/lib/schema";
import { bygherreraadgivningStrings } from "@/i18n/content/bygherreraadgivning";
import { Img, SIZES } from "@/components/Img";

// Icons aligned by index with the phase and value-prop arrays in the catalog.
const phaseIcons: LucideIcon[] = [Lightbulb, Scale, Building2];
const valueIcons: LucideIcon[] = [ShieldCheck, LineChart, Handshake];

/** <head> for the client-consultancy page: pageHead + Service schema +
 *  keywords and Twitter tags. */
export function bygherreraadgivningHead(locale: Locale) {
  const s = bygherreraadgivningStrings[locale];
  const base = pageHead("bygherreraadgivning", locale, s.meta, {
    breadcrumbName: s.hero.title,
    schema: [
      serviceSchema({
        name: s.jsonLd.name,
        serviceType: s.jsonLd.serviceType,
        description: s.jsonLd.description,
        url: BASE_URL + pathFor("bygherreraadgivning", locale),
        audienceType: s.jsonLd.audienceType,
        image: s.meta.ogImage,
      }),
    ],
  });
  return {
    meta: [
      ...base.meta,
      { name: "keywords", content: s.keywords },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: s.twTitle },
      { name: "twitter:description", content: s.twDescription },
      { name: "twitter:image", content: s.meta.ogImage ?? "" },
    ],
    links: base.links,
    scripts: base.scripts,
  };
}

export function BygherreraadgivningPage({ locale }: { locale: Locale }) {
  const t = bygherreraadgivningStrings[locale];

  return (
    <SiteLayout>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <Img
            src="/wp-content/uploads/MISupportIT_NTK4202_1350x900.jpg"
            alt="Bygherrerådgivere fra MI Support på byggeplads"
            className="h-full w-full object-cover"
            sizes={SIZES.full}
            priority
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/85 to-background/40" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 md:px-8 py-24 md:py-36">
          <p className="text-sm uppercase tracking-widest text-primary font-semibold">{t.hero.eyebrow}</p>
          <h1 className="mt-4 text-4xl md:text-6xl font-bold tracking-tight max-w-3xl">{t.hero.title}</h1>
          <p className="mt-6 text-lg md:text-xl text-foreground/85 leading-relaxed max-w-2xl">{t.hero.body}</p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to={pathFor("kontakt", locale)} className="btn-cta">
              {t.hero.ctaPrimary} <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to={pathFor("ydelser", locale)}
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-8 py-3.5 text-base font-semibold text-foreground hover:bg-white/5"
            >
              {t.hero.ctaSecondary}
            </Link>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8 grid gap-12 md:grid-cols-2 items-center">
          <div>
            <p className="text-sm uppercase tracking-widest text-foreground/70">{t.intro.eyebrow}</p>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight">{t.intro.heading}</h2>
            <p className="mt-6 text-lg text-foreground/85 leading-relaxed">{t.intro.p1}</p>
            <p className="mt-4 text-lg text-foreground/85 leading-relaxed">{t.intro.p2}</p>
          </div>
          <Img
            src="/wp-content/uploads/Kontor-gruppe-15_1400x1400.jpg"
            alt="MI Support bygherrerådgivere i dialog med bygherre"
            className="rounded-2xl w-full aspect-[4/3] object-cover"
            sizes={SIZES.split}
          />
        </div>
      </section>

      {/* Value props */}
      <section className="bg-section-light text-section-light-foreground py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="max-w-3xl">
            <p className="text-sm uppercase tracking-widest opacity-70">{t.why.eyebrow}</p>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight">{t.why.heading}</h2>
            <p className="mt-4 text-lg leading-relaxed opacity-90">{t.why.intro}</p>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {t.why.props.map((v, i) => {
              const Icon = valueIcons[i] ?? ShieldCheck;
              return (
                <div key={v.title} className="rounded-2xl bg-background/5 border border-foreground/10 p-8 backdrop-blur">
                  <div className="inline-flex items-center justify-center h-12 w-12 rounded-xl bg-primary/15 text-primary">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-6 text-xl font-bold">{v.title}</h3>
                  <p className="mt-3 leading-relaxed opacity-90">{v.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Phases */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="max-w-3xl">
            <p className="text-sm uppercase tracking-widest text-foreground/70">{t.approach.eyebrow}</p>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight">{t.approach.heading}</h2>
            <p className="mt-6 text-lg text-foreground/85 leading-relaxed">{t.approach.intro}</p>
          </div>

          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {t.approach.phases.map((p, i) => {
              const Icon = phaseIcons[i] ?? Lightbulb;
              return (
                <article key={p.title} className="rounded-3xl bg-secondary/60 border border-white/5 p-8 hover:shadow-2xl hover:shadow-primary/5 transition-shadow">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold uppercase tracking-widest text-primary">{p.label}</span>
                    <Icon className="h-6 w-6 text-foreground/60" />
                  </div>
                  <h3 className="mt-4 text-2xl font-bold">{p.title}</h3>
                  <ul className="mt-6 space-y-3">
                    {p.items.map((it) => (
                      <li key={it} className="flex gap-3 text-foreground/90 leading-relaxed">
                        <span className="mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
                          <Check className="h-3 w-3" />
                        </span>
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sectors */}
      <section className="py-20 bg-secondary/30">
        <div className="mx-auto max-w-7xl px-4 md:px-8 grid gap-12 md:grid-cols-2 items-center">
          <div>
            <p className="text-sm uppercase tracking-widest text-foreground/70">{t.sectors.eyebrow}</p>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight">{t.sectors.heading}</h2>
            <p className="mt-6 text-lg text-foreground/85 leading-relaxed">{t.sectors.intro}</p>
            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {t.sectors.items.map((s) => (
                <li key={s} className="flex items-center gap-3 text-foreground/90">
                  <Check className="h-4 w-4 text-primary" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
          <Img
            src="/wp-content/uploads/MISupportIT_NTK3989_1350x900.jpg"
            alt="Industribyggeri med bygherrerådgivning fra MI Support"
            className="rounded-2xl w-full aspect-[4/3] object-cover"
            sizes={SIZES.split}
          />
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-24">
        <div className="mx-auto max-w-4xl px-4 md:px-8">
          <p className="text-sm uppercase tracking-widest text-foreground/70 text-center">{t.faq.eyebrow}</p>
          <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight text-center">{t.faq.heading}</h2>
          <div className="mt-12 space-y-4">
            {t.faq.items.map((f) => (
              <details key={f.q} className="group rounded-2xl bg-secondary/60 border border-white/5 p-6">
                <summary className="cursor-pointer list-none flex items-center justify-between text-lg font-semibold">
                  {f.q}
                  <span className="ml-4 text-primary transition-transform group-open:rotate-45 text-2xl leading-none whitespace-pre-line">{"\n+"}</span>
                </summary>
                <p className="mt-4 text-foreground/85 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-section-light text-section-light-foreground py-20">
        <div className="mx-auto max-w-4xl px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{t.cta.heading}</h2>
          <p className="mt-6 text-lg leading-relaxed">{t.cta.body}</p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <a
              href="tel:+4555551212"
              className="inline-flex items-center gap-2 rounded-2xl border-2 border-secondary bg-transparent text-secondary px-9 py-4 text-base font-semibold transition-colors hover:bg-secondary hover:text-white"
            >
              {t.cta.call}
            </a>
            <Link to={pathFor("kontakt", locale)} className="btn-cta">
              {t.cta.write} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
